import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from '../../data/mock';
import { MotionButton } from '../ui/motion-button';
import { Typewriter } from '../ui/typewriter';
import { revealVariants, staggerContainer } from '../../utils/motion';

export const Hero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-transparent to-slate-50" />
      </div>

      {/* Floating Elements */}
      <motion.div 
        className="absolute top-20 right-20 w-20 h-20 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full opacity-20"
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 360]
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div 
        className="absolute bottom-32 left-16 w-16 h-16 bg-[#1F3C88] rounded-lg opacity-10"
        animate={{ 
          y: [0, -20, 0],
          rotate: [0, 45, 0]
        }}
        transition={{ 
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            <motion.div variants={revealVariants}>
              <motion.div 
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-6"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.div 
                  className="w-2 h-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
                  animate={{ scale: [1, 1.5, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span>Multi-Sector Consulting Excellence</span>
              </motion.div>
            </motion.div>

            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Typewriter 
                text={mockData.company.tagline}
                speed={50}
                delay={1000}
                className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent"
              />
            </motion.h1>

            <motion.p 
              variants={revealVariants}
              className="text-xl text-gray-600 leading-relaxed max-w-xl"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {mockData.company.description}
            </motion.p>

            <motion.div 
              variants={revealVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <MotionButton
                variant="primary"
                size="lg"
                asChild
              >
                <Link to="/services" className="inline-flex items-center space-x-2 group">
                  <span>Explore Services</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </MotionButton>

              <MotionButton
                variant="secondary"
                size="lg"
                asChild
              >
                <Link to="/request-service" className="inline-flex items-center space-x-2 group">
                  <Play size={16} />
                  <span>Request Service</span>
                </Link>
              </MotionButton>
            </motion.div>

            {/* Animated Stats */}
            <motion.div 
              variants={revealVariants}
              className="flex items-center space-x-8 pt-8 border-t border-gray-200"
            >
              {mockData.stats.slice(0, 3).map((stat, index) => (
                <motion.div 
                  key={index} 
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 2 + index * 0.2 }}
                >
                  <motion.div 
                    className="text-2xl font-bold text-[#1F3C88]"
                    whileHover={{ scale: 1.1 }}
                  >
                    <AnimatedCounter value={stat.value} />
                  </motion.div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Enhanced Visual Element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="relative"
          >
            {/* Logo Animation */}
            <div className="relative w-96 h-96 mx-auto">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-[#1F3C88]/20 to-[#FF5C8A]/20 rounded-full"
                animate={{ 
                  scale: [1, 1.05, 1],
                  rotate: [0, 360] 
                }}
                transition={{ 
                  duration: 20, 
                  repeat: Infinity, 
                  ease: "linear" 
                }}
              />
              
              <motion.div
                className="absolute inset-8 bg-gradient-to-r from-[#FF5C8A]/30 to-[#FF8C42]/30 rounded-full"
                animate={{ 
                  scale: [1.05, 1, 1.05],
                  rotate: [360, 0] 
                }}
                transition={{ 
                  duration: 15, 
                  repeat: Infinity, 
                  ease: "linear" 
                }}
              />
              
              {/* SAX Logo in Center */}
              <motion.div 
                className="absolute inset-0 flex items-center justify-center"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                <motion.img
                  src="https://customer-assets.emergentagent.com/job_sax-advisory/artifacts/1bisi1kh_transparent.png"
                  alt="SAX Logo"
                  className="w-48 h-auto"
                  whileHover={{ scale: 1.05 }}
                />
              </motion.div>
              
              {/* Floating Achievement Icons */}
              {[
                { icon: "200+", label: "Projects", position: "top-8 right-8" },
                { icon: "10+", label: "Years", position: "bottom-8 left-8" },
                { icon: "99%", label: "Success", position: "top-8 left-8" },
                { icon: "12+", label: "Countries", position: "bottom-8 right-8" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className={`absolute ${item.position} w-16 h-16 bg-white rounded-xl shadow-lg flex flex-col items-center justify-center text-xs font-bold text-[#1F3C88]`}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.5 + index * 0.1 }}
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <div className="text-sm">{item.icon}</div>
                  <div className="text-xs text-gray-600">{item.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
      >
        <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center relative">
          <motion.div 
            className="w-1 h-3 bg-gradient-to-b from-[#1F3C88] to-[#FF5C8A] rounded-full mt-2"
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value }) => {
  const [count, setCount] = React.useState(0);
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''));
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      let start = 0;
      const duration = 2000;
      const increment = numericValue / (duration / 16);
      
      const counter = setInterval(() => {
        start += increment;
        if (start >= numericValue) {
          setCount(numericValue);
          clearInterval(counter);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(counter);
    }, 2000); // Start after typewriter effect
    
    return () => clearTimeout(timer);
  }, [numericValue]);
  
  return value.replace(/\d+/, count.toString());
};